import type { SearchType } from '@brightdrop/bd-ui';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { RouteParams } from '../configs/route.config';
import type { ApiResponse, OperationStatus } from './apis/apiResponse.model';
import type { BDError } from './error.model';

export enum ActivationStatus {
  ACTIVE = 'active',
  PENDING = 'pending',
  DISABLED = 'disabled',
  CONTACT = 'contact',
  RETIRED = 'retired',
}
export type PartialEntity<T extends Entity> = Pick<T, keyof Entity> &
  Partial<T>;

// copy all and pick optional ones
export type CopyWithPartialOptional<T, K extends keyof T> = Omit<T, K> &
  Partial<T>;

// TODO: Remove in favor of localization-core `SUPPORTED_LOCALES`.
export enum UserSupportedLocale {
  en_US = 'en-US',
  en_CA = 'en-CA',
  fr_CA = 'fr-CA',
  es_MX = 'es-MX',
}

export type AppSupportedLocale =
  | UserSupportedLocale
  | ('en' | 'fr' | 'es' | 'es-US');

export const MAP_APP_LOCALE_TO_USER_LOCALE = {
  en: UserSupportedLocale.en_US,
  fr: UserSupportedLocale.fr_CA,
  es: UserSupportedLocale.es_MX,
  'es-US': UserSupportedLocale.es_MX,
};

export interface Location {
  latitude: number;
  longitude: number;
}

export interface AssetRawLocation extends Location {
  timestamp?: string;
  required?: boolean;
}

export interface LocationWithAddressString extends Location {
  address?: string;
}

export interface SummaryCounts {
  hubs?: number;
  fleets?: number;
  users?: number;
  assets?: number;
}

export enum SeverityLevel {
  ERROR = 'error',
  WARNING = 'warning',
  INFO = 'info',
  SUCCESS = 'success',
}

export type AggregateCounts<K extends keyof SummaryCounts> = {
  [key: string]: Pick<SummaryCounts, K>;
};
export interface Entity {
  id: string;
  name: string;
}

export interface PaginationInfo {
  page: number;
  rowsPerPage: number;
}

export interface PaginationContinuationInfo {
  rowsPerPage: number;
  continuationToken?: string | null;
}

export interface SkippableStateUpdate {
  skipStateUpdate?: boolean;
}

export interface ListViewSession
  extends PaginationInfo,
    PaginationContinuationInfo {
  count: number;
  sortOrder: { name: string; direction: 'asc' | 'desc' };
  rowsExpanded?: number[];
  selectedIndices?: number[];
  allSelected?: boolean;
}

export interface OperationSession {
  operationStatus?: OperationStatus;
}

export type FormPayload<
  FormType,
  RequiredKey extends keyof RouteParams,
  OptionalKey extends keyof RouteParams = never,
> = {
  formFields: FormType;
  params: Required<Pick<RouteParams, RequiredKey>> &
    Pick<RouteParams, OptionalKey>;
};

export type FormSubmitResponse<T> = Promise<
  FormSubmitSuccess<T> | FormSubmitFailure<T>
>;

export type FormSubmitSuccess<T> = PayloadAction<
  ApiResponse<T> | undefined,
  string,
  { requestStatus: 'fulfilled' }
>;

export type FormSubmitFailure<T> = PayloadAction<
  BDError<ApiResponse<T | null> | null> | undefined,
  string,
  { requestStatus: 'rejected' }
>;

/**
 * Union of keys from T whose types in T extend condition C
 */
export type ConstrainedKeys<T, C> = {
  [K in keyof T]-?: T[K] extends C ? K : never;
}[keyof T];

/**
 * Type consisting of keys from T whose types in T extend condition C
 */
export type ConstrainedType<T, C> = Pick<T, ConstrainedKeys<T, C>>;

/**
 * Picks key union K from T and strips null from any type T[K]
 */
export type NonNullablePick<T, K extends keyof T> = {
  [P in K]: NonNullable<Pick<T, P>[P]>;
};

/**
 * Simple type to use for deeply-nested translation string objects
 */
export type ComponentLabels = string | { [k: string]: ComponentLabels };

export interface SearchCriteria {
  searchType: SearchType;
  input?: string;
}

export interface PagedResult<T> {
  items: T[];
  total_items: number;
  continuation_token?: string;
}

export interface PagedResultWithErrors<T> extends PagedResult<T> {
  errors?: BDError[];
}

export enum DateRange {
  TODAY = 'today',
  YESTERDAY = 'yesterday',
  THIS_WEEK = 'this_week',
  LAST_WEEK = 'last_week',
}

export type TimeZoneOption = {
  value: string;
  label: string;
};

export type ElementTypes<T> = T extends (infer U)[] ? NonNullable<U> : never;

export enum ConnectivityStatusType {
  VERIFICATION_PENDING = 'verificationPending',
  NOT_EQUIPPED = 'notEquipped',
  VERIFIED = 'verified',
  CONNECTION_PENDING = 'connectionPending',
  CONNECTED = 'connected',
}

export enum FileTypes {
  CSV = 'CSV',
}

export interface ContinuationCacheItem<TData = unknown> {
  data: TData;
  continuationToken: string | null;
}

export interface ContinuationCache<TData = unknown> {
  [pageIndex: number]: ContinuationCacheItem<TData>;
}

export type AddressSetting = {
  language: string;
  country: string;
  limit: number;
  sessionToken: string;
};

export enum OnboardingStage {
  STARTED = 'started',
  PENDING = 'pending',
  FINISHED = 'finished',
}

export interface OnboardingSummary {
  totalItems: number;
  successCount: number;
  failureCount: number;
}

export type KeyValueRecord<
  K extends PropertyKey = string,
  T = unknown,
> = Record<K, T>;
